import { Box, Chip, Skeleton, Tab, Tabs, Tooltip } from "@mui/material";
import EAButton from "components/common/EAButton";
import EAIcon from "components/common/EAIcon";
import i18next from "i18next";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ERROR } from "services/common/Constants";
import { PrivilegeEnum, RoleBasedAccessControlContext } from "services/common/RolesUtils";
import { activateDataBlocksForThirdParty, deactivateDataBlocksForThirdParty, getDetailsEsgScore } from "services/edge/DataBlocksService";
import { Context } from "states/Store";
import useStyles from "../../style/js-style/components/thirdparties/PanelEsgStyle";
import { theme } from "style/globalStyle";
import EADialog from "components/common/EADialog";
import EADataBocksEvolutionLineChart from "components/common/charts/EADataBocksEvolutionLineChart";
import { getEsgDepths, getEsgScoresColor, isNoEsgScoring } from 'services/common/DataBlocksUtils';
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import GlobalEsgScoreModal from "./esg/GlobalEsgScoreModal";
import EnvironmentalScoreModal from "./esg/EnvironmentalScoreModal";
import SocialScoreModal from "./esg/SocialScoreModal";
import GovernanceScoreModal from "./esg/GovernanceScoreModal";
import GlobalScoreEsgGauge from "./esg/GlobalScoreEsgGauge";
import EAColorIcon from "components/common/EAColorIcon";



const EsgStopButton = ({arreterESG }) =>{
    const { classes } = useStyles();
    const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);

    const [confirmStopOpen, setConfirmStopOpen] = useState(false);

    const stopHandler = () => {
        arreterESG();
        setConfirmStopOpen(false);
    }

    return (
    <>
        <EAButton 
            colorVariant="errorLight"
            onClick={() => setConfirmStopOpen(true)}
            customStyle={{boxShadow: "none", minHeight: "30px" }}
            disabled={!hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DNB_ESG_SOURCE)}
            className={classes.buttonDesactivation}
            content={
                <span className={classes.buttonStopContent}>
                    <EAIcon icon="stop_scoring"/>
                    {i18next.t("ESG.deactivation")}
                </span>}
        />

         <EADialog isOpen={confirmStopOpen}
            hasActions={false} onClose={() => setConfirmStopOpen(false)} maxWidth="md">
            <div className={classes.dialogStop}>
                <div className={classes.dialogStopContent}>
                    <div className={classes.dialogStopIcon}><EAIcon style={{width: "60%"}} icon="stop_scoring"/></div>
                    <div style={{fontSize: "20px", fontWeight: "600"}}>{i18next.t("ESG.confirmStopTitle")}</div>
                    <div style={{color: theme.palette.grey.grey500, fontWeight: "400", fontSize: "14px", }}>{i18next.t("ESG.confirmStopDescription")}</div>
                </div>
                <div className={classes.dialogStopActions}>
                    <EAButton onClick={() => setConfirmStopOpen(false)} content={i18next.t("cancel")} colorVariant="secondary" />
                    <EAButton onClick={stopHandler} content={i18next.t("validate")} colorVariant="error"/>
                </div>
            </div>
            
        </EADialog>
    </>)
}

const HistoriqueButton = ({evolutionScore}) => {
    const { classes } = useStyles();

    const formatEvolutionData = (evolution) => {
        return evolution?.map(item => {
            // we keep only last five years score
            if(new Date().getFullYear() - 5 > new Date(item.date).getFullYear()) 
                return null;
            return {
              date: new Date(item.date).getTime(), 
              year: new Date(item.date).getFullYear(),
              score: item.score === "null" ? null : parseInt(item.score), 
              text: i18next.t("ESG.score" + item.score +"label"),
              color : getEsgScoresColor(item.score), 
            }
          }).filter(item =>  {
            return item !== null && item.date !== null && item.score !== null && !isNaN(item.score)
          })
    }

    const dataGraph = useMemo(() => {

        return {
            "scoreESG" : formatEvolutionData(evolutionScore?.evolutionScore),
            "scoreEnvironmental" : formatEvolutionData(evolutionScore?.evolutionEnvironmentalScore),
            "scoreSocial" : formatEvolutionData(evolutionScore?.evolutionSocialScore),
            "scoreGovernance" : formatEvolutionData(evolutionScore?.evolutionGovernanceScore)
        }
      }, [evolutionScore])

    const [graphEvolutionOpen, setGraphEvolutionOpen] = useState(false);
    const [selectScoreEvolution, setSelectScoreEvolution] = useState("scoreESG")


    return (
        <>
        <Tooltip title={dataGraph["scoreESG" ]?.length < 2 ? i18next.t("ESG.notEnoughDataForHistoryConstruction"): ""}>
            <EAButton 
                colorVariant="secondary"
                onClick={() => setGraphEvolutionOpen(true)}
                customStyle={{boxShadow: "none", minHeight: "30px" }}
                disabled={dataGraph["scoreESG"]?.length < 2}
                className={classes.buttonHistorique}
                content={
                    <span className={classes.buttonHistoriqueContent}>
                        <EAIcon icon="history"/>
                        {i18next.t("ESG.showHistory")}
                    </span>}
            />
        </Tooltip>

        <EADialog isOpen={graphEvolutionOpen} title={i18next.t("financialStrength.scoreEvolution")}
            hasActions={false} onClose={() => setGraphEvolutionOpen(false)} maxWidth="md">
            <div className={classes.graphEvolutionDialog}>
                <Tabs value={selectScoreEvolution} onChange={(event, newValue) => setSelectScoreEvolution(newValue)} 
                    aria-label="evolution scores ESG tabs" className={classes.graphEvolutionDialogTabsWrapper} >
                    <Tab label={i18next.t("ESG.labelScoreGlobal")} value="scoreESG"  />
                    <Tab label={i18next.t("ESG.riskEnvironment")} value="scoreEnvironmental" />
                    <Tab label={i18next.t("ESG.riskSocial")}  value="scoreSocial"/>
                    <Tab label={i18next.t("ESG.riskGovernance")} value="scoreGovernance" />
                </Tabs>
                <div className={classes.graphEvolutionContent}>
                <EADataBocksEvolutionLineChart id="EADataBocksEvolutionLineChart" data={dataGraph[selectScoreEvolution]}/>
                </div>
            
            </div>
            
        </EADialog>
        </>
    )
}

const IconeEvolutionScore = ({evolution, riskStyle}) => {
    const { classes } = useStyles();

    const evolutionScore = evolution?.evolutionScore;
    
    if(evolutionScore?.length < 2) {
      return(<></>)
    } else {
      // calcul if score is higher or lower
      let isRiskLower = null;
      for (let i = evolutionScore?.length -1; i > 0; i--) {
        if(evolutionScore[i].score !== evolutionScore[i-1].score) {
            isRiskLower = evolutionScore[i].score < evolutionScore[i-1].score;         
          break;
        }
    }
      if(isRiskLower === null) return(<></>);
  
      return (
        <span className={classes.iconeRiskEvolution} style={{backgroundColor: riskStyle.mainColor}}>
          {isRiskLower ? 
           <ArrowDownward style={{color: "#FFF", fontSize: "20px"}} />
          :
           <ArrowUpward style={{color: "#FFF", fontSize: "20px"}}/>
        }
        </span>
      )
    }
  }

const GlobalScoreESG = ({dataESG, evolutionScore, esgDetailsContent}) => {
    const { classes } = useStyles();

    const score = dataESG?.score;

    const [openGlobalDetailsModal, setOpenGlobalDetailsModal] = useState(false);

    const scoreStyle = score === '5' ? 
      {
        background : classes.score5background,
        text: i18next.t("ESG.score5label"),
        mainColor: theme.palette.error.error600, 
        secondaryColor : theme.palette.error.error100, 
      } : score === '4'  ? 
      {
        background : classes.score4background,
        text: i18next.t("ESG.score4label"),
        mainColor: theme.palette.warning.warning600,
        secondaryColor : theme.palette.warning.warning100,  
      } : score === '3' ? 
      {
        background : classes.score3background,
        text: i18next.t("ESG.score3label"),
        mainColor: theme.palette.warning.warning500, 
        textColor: theme.palette.warning.warning600, 
        secondaryColor : theme.palette.warning.warning100, 
      } : score === '2' ? 
      {
        background : classes.score2background,
        text: i18next.t("ESG.score2label"),
        mainColor: theme.palette.primary.primary400, 
        secondaryColor : theme.palette.primary.primary100, 
      } : score === '1' ?
      {
        background : classes.score1background,
        text: i18next.t("ESG.score1label"),
        mainColor: "#2D9F40", 
        secondaryColor : theme.palette.primary.primary200, 
      } :
      {
        background : classes.scoreUnknownBackground,
        text: i18next.t("ESG.scoreUnknownLabel"),
        mainColor: theme.palette.grey.grey400, 
        secondaryColor : theme.palette.grey.grey100, 
      };

    return (
        <div className={classes.scoreGraph}>
            <div className={scoreStyle.background}>
                <div className={classes.scoreLabel}>
                    <span>{i18next.t("ESG.labelScoreGlobal")}</span>
                    <span className={classes.openGlobalDetailsbutton} onClick={() => setOpenGlobalDetailsModal(true)}>
                        {i18next.t("ESG.moreDetails")}<EAIcon icon="call_made"/>
                    </span>
                </div>
                <GlobalScoreEsgGauge score={score} />
                <div className={classes.flexCenter}>
                    <span className={classes.globalScoreText} style={{backgroundColor: scoreStyle.secondaryColor, color: scoreStyle.mainColor}}>
                        {scoreStyle.text}
                        <IconeEvolutionScore riskStyle={scoreStyle} evolution={evolutionScore}/>
                    </span>
                </div>
            </div>
            <GlobalEsgScoreModal isOpen={openGlobalDetailsModal} onClose={() => setOpenGlobalDetailsModal(false)}
                dataESG={dataESG} esgDetailsContent={esgDetailsContent} />
        </div>
    )

}

const ScoreGlobalDetails = ({dataESG}) => {
    const { classes } = useStyles();

    return (
        <div className={classes.scoreGlobalDetails}>
            <div></div>
            <div className={classes.leftBarRisk} style={{borderColor: getEsgScoresColor(dataESG?.score)}}>
                <div>
                    <span style={{fontSize: "31px"}}>{dataESG?.score}</span>
                     <span style={{fontSize: "10px", color: theme.palette.grey.grey400}}>{i18next.t("ESG.overTotal")}</span>
                </div>
                <Chip label={i18next.t("ESG.riskLabel"+ dataESG?.score)} size="small" style={{
                    color: "#FFF",
                    backgroundColor: getEsgScoresColor(dataESG?.score), 
                    fontSize: "10px"
                }} />
                
            </div>
            <div className={classes.globalDataDepth}>{dataESG?.esgDataDepth}</div>
            <div>
                <div className={classes.scoreDetailsSection}>{i18next.t("ESG.scoreDepth")}</div>
                <div style={{fontSize: "10px"}}>{getEsgDepths().find(depth => depth.value ===dataESG?.esgDataDepth).description}</div>
            </div>
        </div>
    )
}

const ScoresDetails = ({dataESG, esgDetailsContent}) => {
    const { classes } = useStyles();

    const [openEnvironmentalDetailsModal, setOpenEnvironmentalDetailsModal] = useState(false);
    const [openSocialDetailsModal, setOpenSocialDetailsModal] = useState(false);
    const [openGovernanceDetailsModal, setOpenGovernanceDetailsModal] = useState(false);

    
    return (
        <div className={classes.scoreDetails}>
            <div>
                <div className={classes.scoreDetailsSection}>{i18next.t("ESG.themes")}</div>

                <div className={classes.scoreDetailsContent}>
                    <span className={classes.squareScore} 
                        style={{backgroundColor: getEsgScoresColor(dataESG?.environmentalRankingScore)}}>
                            {dataESG?.environmentalRankingScore}
                    </span>
                    {i18next.t("ESG.labelEnvironment")}
                    {dataESG?.environmentalRankingScore &&
                        <span className={classes.openDetailsbutton} onClick={() => setOpenEnvironmentalDetailsModal(true)}><EAIcon icon="call_made"/></span>
                    }
                    
                </div>

                <div className={classes.scoreDetailsContent}>
                    <span className={classes.squareScore} 
                    style={{backgroundColor: getEsgScoresColor(dataESG?.socialRankingScore)}}>{dataESG?.socialRankingScore}</span>
                    {i18next.t("ESG.labelSocial")}
                    {dataESG?.socialRankingScore &&
                        <span className={classes.openDetailsbutton} onClick={() => setOpenSocialDetailsModal(true)}><EAIcon icon="call_made"/></span>
                    }
                </div>

                <div className={classes.scoreDetailsContent}>
                    <span className={classes.squareScore}
                    style={{backgroundColor: getEsgScoresColor(dataESG?.governanceRankingScore)}}>{dataESG?.governanceRankingScore}</span>
                    {i18next.t("ESG.labelGovernance")}
                    {dataESG?.governanceRankingScore &&
                        <span className={classes.openDetailsbutton} onClick={() => setOpenGovernanceDetailsModal(true)}><EAIcon icon="call_made"/></span>
                    }
                </div>

            </div>

            <div className={classes.dataDepthSection}>
                <div className={classes.scoreDetailsSection}>{i18next.t("ESG.scoreDepthLabel")}</div>

                {dataESG?.environmentalRankingDataDepth ? 
                <div className={classes.scoreDetailsContent}>
                    <span className={classes.infobulle}>{dataESG?.environmentalRankingDataDepth}</span>
                    <Tooltip title={getEsgDepths().find(depth => depth.value ===dataESG?.environmentalRankingDataDepth).description} placement="top" arrow>
                        <span className={classes.infobulle}><EAIcon icon="info-rounded"/></span>
                        
                    </Tooltip>
                </div> : <div style={{opacity:0}}>.</div>
                }

                {dataESG?.socialRankingDataDepth ? 
                    <div className={classes.scoreDetailsContent}>
                        <span className={classes.infobulle}>{dataESG?.socialRankingDataDepth}</span>
                        <Tooltip title={getEsgDepths().find(depth => depth.value ===dataESG?.socialRankingDataDepth).description} placement="top" arrow>
                        <span className={classes.infobulle}><EAIcon icon="info-rounded" /></span>
                        </Tooltip>
                    </div> : <div style={{opacity:0}}>.</div>
                }

                {dataESG?.governanceRankingDataDepth ?
                    <div className={classes.scoreDetailsContent}>
                        <span className={classes.infobulle}>{dataESG?.governanceRankingDataDepth}</span>
                        <Tooltip title={getEsgDepths().find(depth => depth.value ===dataESG?.governanceRankingDataDepth).description} placement="top" arrow>
                            <span className={classes.infobulle}><EAIcon icon="info-rounded" /></span>
                        </Tooltip>
                    </div> : <div style={{opacity:0}}>.</div>
                }
            </div>    

            <EnvironmentalScoreModal isOpen={openEnvironmentalDetailsModal} onClose={() => setOpenEnvironmentalDetailsModal(false)}
                dataESG={dataESG} esgDetailsContent={esgDetailsContent} /> 
            <SocialScoreModal isOpen={openSocialDetailsModal} onClose={() => setOpenSocialDetailsModal(false)}
                dataESG={dataESG} esgDetailsContent={esgDetailsContent} />    
            <GovernanceScoreModal isOpen={openGovernanceDetailsModal} onClose={() => setOpenGovernanceDetailsModal(false)}
                dataESG={dataESG} esgDetailsContent={esgDetailsContent} />       

        </div>
    )
}

const PanelESG = ({dataESG, thirdPartyId, evolutionScore, onRiskChange}) => {
    const { classes } = useStyles();
    const [state, dispatch] = useContext(Context);
    const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);

    const [dataESGRecu, setDataESGRecu] = useState();
    const [isWaitingScore, setIsWaitingScore] = useState(false);

    //Quand les scores ESG sont activés, on récupère la réponse pour afficher les données, pour ne pas attendre d'avoir recharger tout le thirdparty
    const dataESGToUse = dataESGRecu || dataESG;




    // Activer/Désactiver le scoring des scores D&B ESG pour le tiers
    const declencherESG = async () => {
        setIsWaitingScore(true);
        try {
            const reponse = await activateDataBlocksForThirdParty(state.account.id, thirdPartyId, "ESG");
            setDataESGRecu(reponse.content);
            onRiskChange && onRiskChange();
        } catch (error) {
            dispatch({
                type: "ALERT",
                alert: { type: ERROR, msg: "ACTIVATE_ESG_ERROR" },
            });
        }
        setIsWaitingScore(false);

    }
    const arreterESG = async () => {
        try {
            await deactivateDataBlocksForThirdParty(state.account.id, thirdPartyId, "ESG")
            setDataESGRecu({
                score: null
            })
            onRiskChange && await onRiskChange();
        } catch (error) {
            dispatch({
                type: "ALERT",
                alert: { type: ERROR, msg: "DEACTIVATE_ESG_ERROR" },
            });
        }
    }

    //Récupération des détails du score ESG : 
    const [esgDetailsContent, setesgDetailsContent] = useState();
    
    const fetchEsgDetails = useCallback(async () => {
        const result = await getDetailsEsgScore(state.account.id, thirdPartyId, state.lang);
        setesgDetailsContent(result.content)
    }, [state.account.id, state.lang, thirdPartyId]);

    useEffect(() => {
        if(!isWaitingScore && dataESGToUse && dataESGToUse.score) {
            fetchEsgDetails();
        }
    }, [dataESGToUse, fetchEsgDetails, isWaitingScore])

    //====  JSX =======

    if(isWaitingScore) {
    return (
        <Box className={classes.rootNoData}>
        <div className={classes.skeleton}>
            <Skeleton variant="rect" className={classes.skeletonContent} />
            <Skeleton variant="rect" className={classes.skeletonContent} />
            <Skeleton variant="rect" className={classes.skeletonContent} />
            <Skeleton variant="rect" className={classes.skeletonContent} />
            <Skeleton variant="rect" className={classes.skeletonContent} />
            <Skeleton variant="rect" className={classes.skeletonContent} />
            <Skeleton variant="rect" className={classes.skeletonContent} />
            <Skeleton variant="rect" className={classes.skeletonContent} />
        </div>
        </Box>
    )
    }

    /* Score non activé */
    if(dataESGToUse && isNoEsgScoring(dataESGToUse)) {
        return (
            <Box className={classes.rootNoData}>
                <EAIcon icon="search_off" />
                <div className={classes.noDataText}>
                {i18next.t("ESG.scoreNotActivated")}
                </div>

                <EAButton 
                colorVariant="primaryLight"
                onClick={declencherESG}
                disabled={!hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DNB_ESG_SOURCE)}
                className={classes.button}
                customStyle={{boxShadow: "none"}}
                content={
                    <span className={classes.buttonContent}>
                        <EAColorIcon icon="newEnrichment" />
                        {i18next.t("ESG.activation")}
                    </span>}
                />
            </Box>
            );
    }
    /* Score non évalué par D&B */
    if(dataESGToUse && !dataESGToUse.score && dataESGToUse.noDataAvailable) {
        return (<div className={classes.rootNoData}>
                    <EAIcon icon="notification_important" />
                    <div className={classes.noDataText}>
                    {i18next.t("ESG.dataNoAvailable")}
                    </div>
                    <EsgStopButton arreterESG={arreterESG}/>
                </div>)
    }

    /* Affichage des scores ESG */
    return (
        <>
        <div className={classes.esgContent}>
            <GlobalScoreESG dataESG={dataESGToUse} evolutionScore={evolutionScore} esgDetailsContent={esgDetailsContent}/>
            <div>
                <div className={classes.actionsTopButtons}>
                    <HistoriqueButton evolutionScore={evolutionScore} />
                    <EsgStopButton arreterESG={arreterESG}/>
                </div>
                <div className={classes.esgContent}>
                    <ScoreGlobalDetails dataESG={dataESGToUse} esgDetailsContent={esgDetailsContent}/>
                    <ScoresDetails dataESG={dataESGToUse} esgDetailsContent={esgDetailsContent}/>
                </div>
            </div>

        </div>
        </>

    )


    
}

export default PanelESG;